/*
 * Copyright 2026 Sony Corporation
 */

import { Vector3 } from '@babylonjs/core/Maths/math.vector';
import type { AbstractMesh } from '@babylonjs/core/Meshes/abstractMesh';
import type { Ray } from '@babylonjs/core/Culling/ray';

// Registers the parser `Mesh.Parse` hands a serialized splat to. Without it a
// saved `.babylon` loads with the splat silently missing.
import '@babylonjs/core/Meshes/GaussianSplatting/gaussianSplattingMesh';
// Brings the depth shaders, which the mesh's own import does not: any depth
// pass (ambient occlusion, depth of field) otherwise compiles index.html as GLSL.
import '@babylonjs/core/Materials/GaussianSplatting/gaussianSplattingMaterial';

/**
 * Gaussian splats, where they differ from the rest of the scene.
 *
 * A splat is one mesh whose "vertices" are splats: it has no triangles to pick
 * or to cast a shadow with, and its material is not serialized with it.
 */

/** By class name, so asking does not pull the splat renderer into a bundle that has none. */
export const isSplat = (mesh: AbstractMesh): boolean => mesh.getClassName() === 'GaussianSplattingMesh';

interface SplatMaterial {
  kernelSize: number;
  compensation: boolean;
}

interface SplatMetadata {
  splat?: SplatMaterial;
}

/**
 * Carries the material's antialiasing into the mesh's metadata, which is serialized.
 *
 * The loader sets the kernel from the file's antialiased flag, but the mesh's
 * serializer drops its material, and a parsed one comes back at the default
 * kernel: a splat trained antialiased then renders visibly softer on the
 * Camera page than it did in the editor.
 */
export const recordSplatMaterial = (mesh: AbstractMesh): void => {
  const material = mesh.material as unknown as SplatMaterial | null;
  if (!material) {
    return;
  }
  const metadata = (mesh.metadata ?? {}) as SplatMetadata;
  mesh.metadata = {
    ...metadata,
    splat: { kernelSize: material.kernelSize, compensation: material.compensation },
  } satisfies SplatMetadata;
};

/** The other half of `recordSplatMaterial`, after a `.babylon` load. */
export const restoreSplatMaterial = (mesh: AbstractMesh): void => {
  const recorded = (mesh.metadata as SplatMetadata | null)?.splat;
  const material = mesh.material as unknown as SplatMaterial | null;
  if (!recorded || !material) {
    return;
  }
  material.kernelSize = recorded.kernelSize;
  material.compensation = recorded.compensation;
};

/** The loader's row: position and scale as three floats each, then RGBA and a packed rotation as four bytes each. */
const ROW_BYTES = 32;
/** Half-opaque: fainter splats are haze a click should see through. */
const PICK_MIN_ALPHA = 128;
/** How far from a splat's centre a ray may pass and still hit it, in the splat's largest standard deviation. */
const PICK_SIGMAS = 2;

/**
 * How far along a ray the first opaque splat lies, or undefined when it meets none.
 *
 * `origin` and the unit `direction` are in the splat's own space, as its rows are.
 */
export const nearestSplat = (
  rows: ArrayBuffer,
  [ox, oy, oz]: readonly [number, number, number],
  [dx, dy, dz]: readonly [number, number, number]
): number | undefined => {
  const floats = new Float32Array(rows);
  const bytes = new Uint8Array(rows);
  const count = Math.floor(rows.byteLength / ROW_BYTES);
  const stride = ROW_BYTES / 4;
  let nearest: number | undefined;

  for (let i = 0; i < count; i++) {
    if (bytes[i * ROW_BYTES + 27] < PICK_MIN_ALPHA) {
      continue;
    }
    const f = i * stride;
    const px = floats[f] - ox;
    const py = floats[f + 1] - oy;
    const pz = floats[f + 2] - oz;
    const along = px * dx + py * dy + pz * dz;
    if (along <= 0 || (nearest !== undefined && along >= nearest)) {
      continue;
    }
    const reach = PICK_SIGMAS * Math.max(floats[f + 3], floats[f + 4], floats[f + 5]);
    if (px * px + py * py + pz * pz - along * along < reach * reach) {
      nearest = along;
    }
  }
  return nearest;
};

/**
 * Where a ray meets a splat, in world space.
 *
 * A splat has no triangles for the scene's picking, so this reads the rows the
 * loader kept in RAM (`keepInRam`); a splat without them is not hit.
 */
export const pickSplat = (mesh: AbstractMesh, ray: Ray): { point: Vector3; distance: number } | undefined => {
  const rows = (mesh as unknown as { splatsData: ArrayBuffer | null }).splatsData;
  if (!rows) {
    return undefined;
  }
  const world = mesh.computeWorldMatrix(true);
  const local = world.clone().invert();
  const origin = Vector3.TransformCoordinates(ray.origin, local);
  const direction = Vector3.TransformNormal(ray.direction, local).normalize();
  const along = nearestSplat(rows, [origin.x, origin.y, origin.z], [direction.x, direction.y, direction.z]);
  if (along === undefined) {
    return undefined;
  }
  const point = Vector3.TransformCoordinates(origin.add(direction.scale(along)), world);
  return { point, distance: Vector3.Distance(ray.origin, point) };
};
